import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import apiService from "../services/apiService";
import { mergeAnalyticsPayloads } from "../utils/mergeAnalytics";

const FIGMA_BLUE = "#00A3FF";

function formatCompactInt(n) {
  if (n == null || Number.isNaN(Number(n))) return "0";
  let num = Number(n);
  const sign = num < 0 ? "-" : "";
  num = Math.abs(num);
  if (num < 1000) return sign + String(Math.round(num));
  const tiers = [
    { d: 1e9, s: "B" },
    { d: 1e6, s: "M" },
    { d: 1e3, s: "K" },
  ];
  for (const { d, s } of tiers) {
    if (num >= d) {
      const x = num / d;
      const decimals = x >= 100 ? 0 : x >= 10 ? 1 : 2;
      return sign + String(Number(x.toFixed(decimals))) + s;
    }
  }
  return sign + String(Math.round(num));
}

/**
 * Derive today's top chains from the per-date / per-chain breakdown that
 * the analytics endpoint already returns. `by_date_chain` is an array of
 * objects shaped like `{ date: "2026-05-11", "Ethereum": 1234, "Solana":
 * 567, ... }`. We take the most recent entry (today's row, populated by
 * the hourly rollup task as the day progresses), strip the date key,
 * rank the remaining chain → count pairs, and compute percentages
 * relative to that day's total. Falling back gracefully when there's no
 * traffic yet for today keeps the card honest before the first request.
 */
function todaysTopChains(byDateChain) {
  const rows = Array.isArray(byDateChain) ? byDateChain : [];
  if (!rows.length) return [];
  const today = [...rows].sort((a, b) =>
    String(a.date).localeCompare(String(b.date)),
  )[rows.length - 1];
  if (!today) return [];
  const entries = Object.entries(today)
    .filter(([k, v]) => k !== "date" && Number(v) > 0)
    .map(([chain, count]) => ({ chain, count: Number(count) }));
  const sumTotal = entries.reduce((s, e) => s + e.count, 0) || 1;
  return entries
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .map((e) => ({
      chain: e.chain,
      count: e.count,
      percentage: Math.round((e.count / sumTotal) * 1000) / 10,
    }));
}

function TopChainsCard({ byDateChain, loading }) {
  const top = todaysTopChains(byDateChain);
  const hasData = top.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="relative rounded-sm bg-[#1a1a1a] p-5 min-h-[188px]"
    >
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "linear-gradient(0deg, #0E0E0E 0%, rgba(14, 14, 14, 0) 50%, rgba(14, 14, 14, 0) 100%)",
        }}
      />
      <div className="relative z-10 flex gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-[#00A3FF]/15 text-[#00A3FF]">
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-title text-lg font-semibold text-white">
            Top chains{" "}
            <span className="font-secondary text-xs font-normal uppercase tracking-[0.15em] text-white/40">
              today
            </span>
          </h4>
          {loading ? (
            <ul className="mt-3 space-y-2" aria-label="Loading top chains">
              {[0, 1, 2].map((i) => (
                <li
                  key={i}
                  className="flex items-center gap-3"
                >
                  <div className="h-3 min-w-0 flex-1 animate-pulse rounded bg-white/[0.06]" />
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-white/[0.04]" />
                  <div className="h-3 w-10 animate-pulse rounded bg-white/[0.06]" />
                </li>
              ))}
            </ul>
          ) : !hasData ? (
            <p className="font-secondary mt-2 text-md leading-relaxed text-[#888888]">
              Today's chain breakdown updates hourly — first refresh pending.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {top.map((c) => (
                <li
                  key={c.chain}
                  className="font-secondary flex items-center gap-3 text-sm text-white/85"
                >
                  <div className="min-w-0 flex-1 truncate font-medium">
                    {c.chain}
                  </div>
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-white/[0.08]">
                    <motion.div
                      className="h-full origin-left rounded-full"
                      style={{ backgroundColor: FIGMA_BLUE }}
                      initial={{ scaleX: 0 }}
                      animate={{
                        scaleX: Math.min(1, Number(c.percentage) / 100 || 0),
                      }}
                      transition={{
                        duration: 0.9,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    />
                  </div>
                  <div className="w-12 shrink-0 text-right font-mono text-xs text-[#00A3FF]">
                    {Number(c.percentage).toFixed(1)}%
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function TotalRequestsCard({ totalRequests, dailyAverage, loading }) {
  const total = Number(totalRequests) || 0;
  const avg = Number(dailyAverage) || 0;
  const hasData = total > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.05 }}
      className="relative min-h-[188px] overflow-hidden rounded-sm bg-[#1a1a1a] p-5 h-full w-full"
    >
      <div
        className="pointer-events-none absolute inset-0 z-20"
        style={{
          background:
            "linear-gradient(0deg, #0E0E0E 0%, rgba(14, 14, 14, 0) 0%, rgba(14, 14, 14, 0) 100%)",
        }}
      />
      <img
        src="/images/background_card.png"
        alt=""
        className="absolute inset-0 z-10 h-full w-full object-cover grayscale brightness-[0.48] contrast-[1.05]"
      />
      <div className="relative z-20 w-full h-full flex flex-col item-end justify-end">
        <p className="font-secondary text-sm font-semibold uppercase tracking-[0.2em] text-primary">
          Total requests (30d)
        </p>
        <p className="font-title mt-2 text-3xl font-bold tracking-tight text-white">
          {loading ? "…" : hasData ? formatCompactInt(total) : "0"}{" "}
          <span className="text-sm font-title font-normal text-white/75">
            requests
          </span>
        </p>
        {hasData ? (
          <p className="font-secondary mt-1 text-xs text-white/55">
            {formatCompactInt(avg)} / day average
          </p>
        ) : null}
      </div>
    </motion.div>
  );
}

/**
 * Two-card insight strip beneath the API keys list. Pulls per-org data
 * from /api/chains/api-keys/:uuid/analytics/ (same call as the right-rail
 * usage stats) and surfaces the two cuts that earn the largest surface:
 *
 *   - **Top chains today** — chain ranking for the current calendar day,
 *     derived from `by_date_chain`. More actionable than a 30-day rollup
 *     when usage shifts day to day.
 *   - **Total requests (30d)** — the headline volume number, lifted out
 *     of the right-rail Usage Statistics to give it room to breathe.
 *     The right-rail keeps the quota-oriented metrics (daily / monthly /
 *     full-node / archive).
 */
export default function DashboardInsights({ apiKeys }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!apiKeys?.length) {
      setAnalytics(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const settled = await Promise.allSettled(
        apiKeys.map((k) => apiService.getApiKeyAnalytics(k.uuid, "30d")),
      );
      const ok = settled
        .filter((r) => r.status === "fulfilled" && r.value)
        .map((r) => r.value);
      setAnalytics(ok.length ? mergeAnalyticsPayloads(ok) : null);
    } catch {
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [apiKeys]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="grid grid-cols-2 gap-4 items-start max-h-[200px] relative">
      <TopChainsCard
        byDateChain={analytics?.by_date_chain}
        loading={loading}
      />
      <TotalRequestsCard
        totalRequests={analytics?.total_requests}
        dailyAverage={analytics?.daily_average}
        loading={loading}
      />
    </div>
  );
}
